import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { wishlistAPI, vehiclesAPI, enumsAPI, modsAPI, subscriptionAPI, customCategoriesAPI } from "@/lib/api";
import { HeaderIcon } from "@/lib/assets";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Heart,
  Trash2,
  Edit,
  ExternalLink,
  Check,
  DollarSign,
  ShoppingCart,
  Filter,
  Truck,
  Wrench,
  Crown,
} from "lucide-react";

export default function Wishlist() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [showPurchased, setShowPurchased] = useState(false);
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterVehicle, setFilterVehicle] = useState("all");
  const [isPro, setIsPro] = useState(false);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Convert to Mod dialog state
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertingItem, setConvertingItem] = useState(null);
  const [selectedVehicleForMod, setSelectedVehicleForMod] = useState("");
  const [converting, setConverting] = useState(false);
  
  // Custom category state
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [addingCategory, setAddingCategory] = useState(false);
  
  const [form, setForm] = useState({
    name: "",
    category: "Other",
    brand: "",
    part_number: "",
    estimated_cost: "",
    vendor: "",
    vendor_link: "",
    priority: "Medium",
    notes: "",
    vehicle_id: "",
  });

  useEffect(() => {
    loadData();
  }, [showPurchased]);

  const loadData = async () => {
    try {
      const [wishlistRes, vehiclesRes, categoriesRes, subRes] = await Promise.all([
        wishlistAPI.list({ show_purchased: showPurchased }),
        vehiclesAPI.list(),
        enumsAPI.modCategories(),
        subscriptionAPI.status(),
      ]);
      setItems(wishlistRes.data.items);
      setTotalCost(wishlistRes.data.total_estimated_cost);
      setVehicles(vehiclesRes.data);
      setCategories(categoriesRes.data);
      setIsPro(subRes.data.is_pro);
    } catch (error) {
      toast.error("Failed to load wishlist");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) { toast.error("Category name is required"); return; }
    setAddingCategory(true);
    try {
      await customCategoriesAPI.create({ name: trimmed, type: "mod" });
      toast.success(`Category "${trimmed}" added`);
      const categoriesRes = await enumsAPI.modCategories();
      setCategories(categoriesRes.data);
      setForm({ ...form, category: trimmed });
      setNewCategoryName("");
      setAddCategoryOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add category");
    } finally {
      setAddingCategory(false);
    }
  };


  const resetForm = () => {
    setForm({
      name: "",
      category: "Other",
      brand: "",
      part_number: "",
      estimated_cost: "",
      vendor: "",
      vendor_link: "",
      priority: "Medium",
      notes: "",
      vehicle_id: "",
    });
    setEditingItem(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setForm({
      name: item.name,
      category: item.category || "Other",
      brand: item.brand || "",
      part_number: item.part_number || "",
      estimated_cost: item.estimated_cost || "",
      vendor: item.vendor || "",
      vendor_link: item.vendor_link || "",
      priority: item.priority || "Medium",
      notes: item.notes || "",
      vehicle_id: item.vehicle_id || "",
    });
    setEditingItem(item);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Item name is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        estimated_cost: form.estimated_cost ? parseFloat(form.estimated_cost) : 0,
        vehicle_id: form.vehicle_id || null,
      };

      if (editingItem) {
        await wishlistAPI.update(editingItem.id, payload);
        toast.success("Item updated");
      } else {
        await wishlistAPI.create(payload);
        toast.success("Item added to wishlist");
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save item");
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePurchased = async (item) => {
    try {
      await wishlistAPI.togglePurchased(item.id);
      toast.success(item.purchased ? "Marked as not purchased" : "Marked as purchased! 🎉");
      loadData();
    } catch (error) {
      toast.error("Failed to update item");
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}" from wishlist?`)) return;
    
    try {
      await wishlistAPI.delete(item.id);
      toast.success("Item removed from wishlist");
      loadData();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  // Convert wishlist item to mod (Pro feature)
  const openConvertDialog = (item) => {
    if (!isPro) {
      toast.info("Converting to Mod is a Pro feature");
      navigate("/subscription");
      return;
    }
    if (vehicles.length === 0) {
      toast.error("Please add a vehicle first");
      return;
    }
    setConvertingItem(item);
    setSelectedVehicleForMod(item.vehicle_id || "");
    setConvertDialogOpen(true);
  };

  const handleConvertToMod = async () => {
    if (!selectedVehicleForMod) {
      toast.error("Please select a vehicle");
      return;
    }

    setConverting(true);
    try {
      // Create mod from wishlist item
      const modData = {
        vehicle_id: selectedVehicleForMod,
        category: convertingItem.category || "Other",
        item_name: convertingItem.name,
        brand: convertingItem.brand || null,
        part_number: convertingItem.part_number || null,
        install_date: (() => {
          const d = new Date();
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        })(),
        cost: convertingItem.estimated_cost || 0,
        vendor: convertingItem.vendor || null,
        vendor_link: convertingItem.vendor_link || null,
        notes: convertingItem.notes || null,
        tags: [],
        photos: [],
      };

      await modsAPI.create(modData);
      
      // Mark wishlist item as purchased
      await wishlistAPI.togglePurchased(convertingItem.id);
      
      toast.success(`"${convertingItem.name}" added to mods and marked as purchased! 🎉`);
      setConvertDialogOpen(false);
      setConvertingItem(null);
      setSelectedVehicleForMod("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert to mod");
    } finally {
      setConverting(false);
    }
  };

  const filteredItems = items.filter((item) => {
    if (filterPriority !== "all" && item.priority !== filterPriority) return false;
    if (filterVehicle !== "all" && item.vehicle_id !== filterVehicle) return false;
    return true;
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High": return "text-red-500 bg-red-50 border-red-200";
      case "Medium": return "text-amber-500 bg-amber-50 border-amber-200";
      case "Low": return "text-green-500 bg-green-50 border-green-200";
      default: return "text-gray-500 bg-gray-50 border-gray-200";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <HeaderIcon />
                <span className="font-bold text-lg hidden sm:block">Buildsheet Pro</span>
              </Link>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Garage
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title & Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Heart className="w-6 h-6 text-red-500" />
              My Wishlist
            </h1>
            <p className="text-muted-foreground mt-1">
              {filteredItems.length} item{filteredItems.length !== 1 ? "s" : ""} • 
              ${totalCost.toLocaleString()} estimated total
            </p>
          </div>
          <Button onClick={openAddDialog} data-testid="add-wishlist-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Item
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterVehicle} onValueChange={setFilterVehicle}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Vehicle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Vehicles</SelectItem>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.nickname}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="show-purchased"
                  checked={showPurchased}
                  onCheckedChange={setShowPurchased}
                />
                <label htmlFor="show-purchased" className="text-sm cursor-pointer">
                  Show purchased
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Wishlist Items */}
        {filteredItems.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <ShoppingCart className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No items yet</h3>
              <p className="text-muted-foreground mt-1">
                Start adding parts and mods you want to buy!
              </p>
              <Button onClick={openAddDialog} className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Add First Item
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredItems.map((item) => (
              <Card 
                key={item.id} 
                className={`relative ${item.purchased ? "opacity-60" : ""}`}
                data-testid={`wishlist-item-${item.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <CardTitle className={`text-lg ${item.purchased ? "line-through" : ""}`}>
                        {item.name}
                      </CardTitle>
                      {item.brand && (
                        <p className="text-sm text-muted-foreground">{item.brand}</p>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full border ${getPriorityColor(item.priority)}`}>
                      {item.priority}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    {item.estimated_cost > 0 && (
                      <div className="flex items-center gap-2 text-lg font-semibold text-primary">
                        <DollarSign className="w-4 h-4" />
                        ${item.estimated_cost.toLocaleString()}
                      </div>
                    )}
                    {item.vehicle_nickname && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Truck className="w-4 h-4" />
                        {item.vehicle_nickname}
                      </div>
                    )}
                    {item.category && (
                      <p className="text-muted-foreground">Category: {item.category}</p>
                    )}
                    {item.part_number && (
                      <p className="text-muted-foreground">P/N: {item.part_number}</p>
                    )}
                    {item.vendor && (
                      <p className="text-muted-foreground">
                        Vendor: {item.vendor}
                        {item.vendor_link && (
                          <a 
                            href={item.vendor_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center ml-1 text-primary hover:underline"
                          >
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </p>
                    )}
                    {item.notes && (
                      <p className="text-muted-foreground line-clamp-2">{item.notes}</p>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                    <Button
                      size="sm"
                      variant={item.purchased ? "outline" : "default"}
                      onClick={() => handleTogglePurchased(item)}
                      className="flex-1"
                      data-testid={`toggle-purchased-${item.id}`}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      {item.purchased ? "Undo Purchase" : "Mark Purchased"}
                    </Button>
                    {!item.purchased && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => openConvertDialog(item)}
                        title={isPro ? "Add to Mods" : "Pro feature - Add to Mods"}
                        className={!isPro ? "opacity-60" : ""}
                      >
                        <Wrench className="w-4 h-4" />
                        {!isPro && <Crown className="w-3 h-3 ml-1 text-amber-500" />}
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openEditDialog(item)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(item)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Edit Item" : "Add to Wishlist"}</DialogTitle>
            <DialogDescription>
              {editingItem ? "Update your wishlist item" : "Add a part or mod you want to buy"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Item Name *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., ARB Front Bumper"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <div className="flex gap-2">
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="flex-1" data-testid="wishlist-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isPro && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={() => setAddCategoryOpen(true)}
                      title="Add custom category"
                      data-testid="wishlist-add-category-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="brand">Brand</Label>
                <Input
                  id="brand"
                  value={form.brand}
                  onChange={(e) => setForm({ ...form, brand: e.target.value })}
                  placeholder="e.g., ARB"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="part_number">Part Number</Label>
                <Input
                  id="part_number"
                  value={form.part_number}
                  onChange={(e) => setForm({ ...form, part_number: e.target.value })}
                  placeholder="e.g., 3421530"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="estimated_cost">Estimated Cost ($)</Label>
              <Input
                id="estimated_cost"
                type="number"
                value={form.estimated_cost}
                onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="vehicle_id">For Vehicle (optional)</Label>
              <Select value={form.vehicle_id || "none"} onValueChange={(v) => setForm({ ...form, vehicle_id: v === "none" ? "" : v })}>
                <SelectTrigger>
                  <SelectValue placeholder="General / No specific vehicle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">General / No specific vehicle</SelectItem>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.nickname}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vendor">Vendor</Label>
                <Input
                  id="vendor"
                  value={form.vendor}
                  onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                  placeholder="e.g., 4WP"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vendor_link">Vendor Link</Label>
                <Input
                  id="vendor_link"
                  value={form.vendor_link}
                  onChange={(e) => setForm({ ...form, vendor_link: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingItem ? "Save Changes" : "Add to Wishlist"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Mod Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-primary" />
              Add to Mods
            </DialogTitle>
            <DialogDescription>
              Add "{convertingItem?.name}" to a vehicle's mod list
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label htmlFor="vehicle-select" className="mb-2 block">Select Vehicle</Label>
            <Select value={selectedVehicleForMod} onValueChange={setSelectedVehicleForMod}>
              <SelectTrigger id="vehicle-select">
                <SelectValue placeholder="Choose a vehicle..." />
              </SelectTrigger>
              <SelectContent>
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4" />
                      {v.nickname}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {convertingItem && (
              <div className="mt-4 p-3 bg-muted rounded-lg text-sm">
                <p><strong>Item:</strong> {convertingItem.name}</p>
                {convertingItem.brand && <p><strong>Brand:</strong> {convertingItem.brand}</p>}
                {convertingItem.estimated_cost > 0 && (
                  <p><strong>Cost:</strong> ${convertingItem.estimated_cost.toLocaleString()}</p>
                )}
                <p className="text-muted-foreground mt-2 text-xs">
                  This will create a mod entry and mark the wishlist item as purchased.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConvertToMod} disabled={converting || !selectedVehicleForMod}>
              {converting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Wrench className="w-4 h-4 mr-2" />
              Add to Mods
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Custom Category Dialog */}
      <Dialog open={addCategoryOpen} onOpenChange={setAddCategoryOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              Add Custom Category
            </DialogTitle>
            <DialogDescription>Create a new category for your wishlist and build sheets.</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <Label htmlFor="new-wishlist-cat">Category Name</Label>
            <Input
              id="new-wishlist-cat"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="e.g., Communications, Recovery Gear"
              data-testid="wishlist-new-category-input"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddCategory(); } }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCategoryOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAddCategory}
              disabled={addingCategory || !newCategoryName.trim()}
              data-testid="wishlist-save-category-btn"
            >
              {addingCategory && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
